import React, { useState } from "react";
import { Badge, Row, Col, Collapse, Spinner } from "reactstrap";
import { ChevronDown, ChevronUp, Globe, CheckCircle, XCircle, Info, MapPin, Phone, Star, ExternalLink, Navigation } from "react-feather";
import axios from "axios";
import { toast } from "react-toastify";
import { AI_BACKEND_API_2 } from "../../Config/secrets";

const ProfileMoreInfoTab = ({ profile, type, onReload }) => {
  const [expandedSections, setExpandedSections] = useState({
    summary: true,
    brands: true,
    sources: true,
    locations: true,
  });
  const [fetchingLocations, setFetchingLocations] = useState(false);

  const sources = type === 'retailer' ? profile?.retailer_sources : profile?.brand_sources;
  const storeLocations = profile?.store_locations || [];

  const handleFetchLocations = async () => {
    setFetchingLocations(true);
    try {
      const response = await axios({
        method: "POST",
        url: `${AI_BACKEND_API_2}/get_store_location`,
        data: { id: profile?.id, type },
      });

      if (response.data?.status === 200 && response.data?.record) {
        toast.success(response.data?.msg || "Store locations fetched successfully");
        if (onReload) {
          await onReload();
        }
      } else {
        toast.info(response.data?.msg || "No store locations found");
      }
    } catch (err) {
      console.error("Error fetching store locations:", err.message);
      toast.error(err?.response?.data?.msg || err?.message || "Failed to fetch store locations");
    } finally {
      setFetchingLocations(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const formatKey = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
      .trim();
  };

  // Check if value is coverage_assessment type object
  const isCoverageAssessment = (key, value) => {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
    const keys = Object.keys(value);
    return keys.some(k => ["well_covered", "partially_covered", "missing_or_limited", "classification_notes"].includes(k));
  };

  // Render coverage assessment in a simple table format
  const renderCoverageAssessment = (value) => {
    const rows = [
      { key: "well_covered", label: "Well Covered", color: "#28a745" },
      { key: "partially_covered", label: "Partially Covered", color: "#ff9800" },
      { key: "missing_or_limited", label: "Missing or Limited", color: "#dc3545" },
    ];

    return (
      <div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <tbody>
            {rows.map(({ key, label, color }) => {
              const items = value[key];
              if (!items || (Array.isArray(items) && items.length === 0)) return null;

              return (
                <tr key={key} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "10px 0", width: "160px", color, fontWeight: 500 }}>
                    {label}
                  </td>
                  <td style={{ padding: "10px 0", color: "#333" }}>
                    {(Array.isArray(items) ? items : [items]).join(", ")}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {value.classification_notes && (
          <div style={{ marginTop: "12px", padding: "12px", backgroundColor: "#f8f9fa", borderRadius: "6px", borderLeft: "3px solid #508CFF" }}>
            <div style={{ fontSize: "12px", color: "#666", fontWeight: 600, marginBottom: "4px" }}>Classification Notes</div>
            <div style={{ fontSize: "14px", color: "#333", lineHeight: "1.5" }}>{value.classification_notes}</div>
          </div>
        )}
      </div>
    );
  };

  const renderValue = (value, key) => {
    if (value === null || value === undefined) {
      return <span style={{ color: "#999", fontStyle: "italic" }}>Not available</span>;
    }

    if (typeof value === "boolean") {
      return value ? (
        <Badge color="success">Yes</Badge>
      ) : (
        <Badge color="secondary">No</Badge>
      );
    }

    if (typeof value === "number") {
      return <span style={{ fontWeight: 600, color: "#333" }}>{value.toLocaleString()}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span style={{ color: "#999", fontStyle: "italic" }}>Empty</span>;
      }
      return (
        <div className="d-flex flex-wrap gap-1">
          {value.map((item, index) => (
            <Badge key={index} color="primary">
              {typeof item === "object" ? JSON.stringify(item) : String(item)}
            </Badge>
          ))}
        </div>
      );
    }

    // Check for coverage_assessment type objects
    if (isCoverageAssessment(key, value)) {
      return renderCoverageAssessment(value);
    }

    if (typeof value === "object") {
      return (
        <div style={{
          backgroundColor: "#f8f9fa",
          padding: "12px",
          borderRadius: "8px",
          fontSize: "13px",
          maxHeight: "200px",
          overflow: "auto"
        }}>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      );
    }

    if (typeof value === "string" && (value.startsWith("http://") || value.startsWith("https://"))) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: "#508CFF", textDecoration: "none" }}>
          {value}
        </a>
      );
    }

    return <span>{String(value)}</span>;
  };

  // Define the order of keys for summary section
  const summaryKeyOrder = [
    "type",
    "entity",
    "category",
    "word_count",
    'database_classification',
    'fashion_classification',
    'category_upgrade',
    "pages_analyzed",
    "category_status",
    "inclusion_criteria",
    'database_reason',
    'fashion_reason',
    'category_reason_upgrade',
    "type_reason",
    "entity_reason",
    "category_reason",
    "coverage_assessment",
    "summary",
  ];

  // Keys that should take full width
  const fullWidthKeys = ["coverage_assessment", "summary", "type_reason", "entity_reason", "category_reason"];

  const renderSummarySection = (summary) => {
    if (!summary || (typeof summary === "object" && Object.keys(summary).length === 0)) {
      return (
        <div className="text-center py-4">
          <Info size={48} color="#ccc" />
          <p style={{ color: "#999", marginTop: "12px" }}>No summary data available</p>
        </div>
      );
    }

    const entries = typeof summary === "object" ? Object.entries(summary) : [];

    // Sort entries based on defined order
    const sortedEntries = [...entries].sort((a, b) => {
      const indexA = summaryKeyOrder.indexOf(a[0]);
      const indexB = summaryKeyOrder.indexOf(b[0]);
      // If key not in order list, put it at the end
      const orderA = indexA === -1 ? summaryKeyOrder.length : indexA;
      const orderB = indexB === -1 ? summaryKeyOrder.length : indexB;
      return orderA - orderB;
    });

    return (
      <Row>
        {sortedEntries.map(([key, value], index) => {
          const isFullWidth = fullWidthKeys.includes(key) || isCoverageAssessment(key, value);
          const colSize = isFullWidth ? 12 : 6;
          return (
            <Col md={colSize} key={index} className="mb-3">
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                  padding: "16px",
                  height: "100%",
                }}
              >
                <div style={{ fontSize: "11px", color: "#666", textTransform: "uppercase", marginBottom: "6px", fontWeight: 600 }}>
                  {formatKey(key)}
                </div>
                {(key === 'fashion_classification' || key === 'category_status') ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      padding: '5px',
                      maxWidth: '100px',
                      color: (value?.toLowerCase() === 'fashion' || value?.toLowerCase() === 'include') ? '#278c08' : '#FF9A07',
                      backgroundColor: (value?.toLowerCase() === 'fashion' || value?.toLowerCase() === 'include') ? '#E6F7EE' : '#FFF3E0'
                    }}>
                    {renderValue(value, key)}
                  </div>
                ) : (
                  <div>{renderValue(value, key)}</div>
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Keys to skip in brands grid (shown separately)
  const brandSkipKeys = ["brands", "more_brand_names"];

  // Keys that should be full width in brands section
  const brandFullWidthKeys = ["analysis_notes", "page_organization"];

  // Render a brands list (reusable for main brands and more_brand_names)
  const renderBrandsList = (brandsArray, title) => {
    if (!brandsArray || !Array.isArray(brandsArray) || brandsArray.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          backgroundColor: "#fff",
          border: "1px solid #e9ecef",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "16px",
        }}
      >
        <div style={{ fontSize: "11px", color: "#666", textTransform: "uppercase", marginBottom: "12px", fontWeight: 600 }}>
          {title} ({brandsArray.length})
        </div>
        <div className="d-flex flex-wrap gap-2">
          {brandsArray.map((brand, index) => (
            <a
              key={index}
              href={brand.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                padding: "6px 12px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: "20px",
                fontSize: "13px",
                color: "#333",
                textDecoration: "none",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#508CFF";
                e.target.style.color = "#fff";
                e.target.style.borderColor = "#508CFF";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#f8f9fa";
                e.target.style.color = "#333";
                e.target.style.borderColor = "#e0e0e0";
              }}
            >
              {brand.brand_name}
            </a>
          ))}
        </div>
      </div>
    );
  };

  // Render grid for metadata keys
  const renderMetadataGrid = (entries, fullWidthKeys) => {
    if (!entries || entries.length === 0) return null;

    return (
      <Row className="mb-3">
        {entries.map(([key, value], index) => {
          const isFullWidth = fullWidthKeys.includes(key);
          const colSize = isFullWidth ? 12 : 6;
          return (
            <Col md={colSize} key={index} className="mb-3">
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                  padding: "16px",
                  height: "100%",
                }}
              >
                <div style={{ fontSize: "11px", color: "#666", textTransform: "uppercase", marginBottom: "6px", fontWeight: 600 }}>
                  {formatKey(key)}
                </div>
                <div>{renderValue(value, key)}</div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  // Render sources list: each item has source_id and sources { name, logo }
  const renderSourcesSection = (sourcesList) => {
    if (!sourcesList || !Array.isArray(sourcesList) || sourcesList.length === 0) {
      return (
        <div className="text-center py-4">
          <Info size={48} color="#ccc" />
          <p style={{ color: "#999", marginTop: "12px" }}>No sources data available</p>
        </div>
      );
    }

    return (
      <div className="d-flex flex-wrap gap-3">
        {sourcesList.map((entry, index) => {
          const { source_id, sources: sourceInfo } = entry;
          const name = sourceInfo?.name ?? "Unknown";
          const logo = sourceInfo?.logo;

          return (
            <div
              key={source_id ?? index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 16px",
                backgroundColor: "#fff",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                minWidth: "180px",
              }}
            >
              {logo && (
                <img
                  src={logo}
                  alt={name}
                  style={{
                    width: 40,
                    height: 40,
                    objectFit: "contain",
                    borderRadius: "6px",
                    backgroundColor: "#f8f9fa",
                  }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              <div>
                <div style={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>{name}</div>
                {source_id != null && (
                  <div style={{ fontSize: "12px", color: "#666" }}>ID: {source_id}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStoreLocationsSection = () => {
    if (!storeLocations || storeLocations.length === 0) {
      return (
        <div className="text-center py-5">
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#f5f5f5",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "12px",
            }}
          >
            <MapPin size={24} color="#9ca3af" />
          </div>
          <p style={{ color: "#6b7280", marginBottom: "16px", fontSize: "14px" }}>No store locations available</p>
          <button
            onClick={handleFetchLocations}
            disabled={fetchingLocations}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 20px",
              backgroundColor: "#1a1a1a",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 500,
              cursor: fetchingLocations ? "not-allowed" : "pointer",
              opacity: fetchingLocations ? 0.7 : 1,
              transition: "background-color 0.15s ease",
            }}
            onMouseEnter={(e) => !fetchingLocations && (e.currentTarget.style.backgroundColor = "#333")}
            onMouseLeave={(e) => !fetchingLocations && (e.currentTarget.style.backgroundColor = "#1a1a1a")}
          >
            {fetchingLocations ? (
              <>
                <Spinner size="sm" style={{ width: "14px", height: "14px" }} />
                Fetching...
              </>
            ) : (
              <>
                <MapPin size={14} />
                Fetch Store Locations
              </>
            )}
          </button>
        </div>
      );
    }

    return (
      <div>
        <Row className="g-3">
          {storeLocations.map((location, index) => (
            <Col lg={6} xl={4} key={location.id || index}>
              <div
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #eaeaea",
                  borderRadius: "10px",
                  overflow: "hidden",
                  height: "100%",
                  transition: "box-shadow 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Location Header */}
                <div style={{ padding: "16px 16px 12px" }}>
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h6 style={{ fontWeight: 600, marginBottom: "2px", color: "#1a1a1a", fontSize: "14px" }}>
                        {location.name || profile?.name || "Store Location"}
                      </h6>
                      {location.city && (
                        <span style={{ fontSize: "12px", color: "#888" }}>
                          {[location.city, location.state, location.country].filter(Boolean).join(", ")}
                        </span>
                      )}
                    </div>
                    {location.business_status && (
                      <span
                        style={{
                          fontSize: "10px",
                          padding: "3px 8px",
                          borderRadius: "4px",
                          fontWeight: 500,
                          backgroundColor: location.business_status === "OPERATIONAL" ? "#f0fdf4" : "#fefce8",
                          color: location.business_status === "OPERATIONAL" ? "#166534" : "#854d0e",
                        }}
                      >
                        {location.business_status === "OPERATIONAL" ? "Active" : location.business_status}
                      </span>
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: "1px", backgroundColor: "#f5f5f5", margin: "0 16px" }} />

                {/* Location Body */}
                <div style={{ padding: "12px 16px 16px" }}>
                  {/* Address */}
                  {location.formatted_address && (
                    <div className="d-flex align-items-start gap-2 mb-2">
                      <MapPin size={14} color="#9ca3af" style={{ marginTop: "2px", flexShrink: 0 }} />
                      <span style={{ fontSize: "12px", color: "#6b7280", lineHeight: "1.5" }}>
                        {location.formatted_address}
                      </span>
                    </div>
                  )}

                  {/* Phone & Rating Row */}
                  <div className="d-flex align-items-center gap-4 mb-3" style={{ flexWrap: "wrap" }}>
                    {location.phone && (
                      <div className="d-flex align-items-center gap-1">
                        <Phone size={13} color="#9ca3af" />
                        <a
                          href={`tel:${location.phone}`}
                          style={{ fontSize: "12px", color: "#374151", textDecoration: "none" }}
                        >
                          {location.phone}
                        </a>
                      </div>
                    )}
                    {location.rating && (
                      <div className="d-flex align-items-center gap-1">
                        <Star size={13} color="#9ca3af" fill="#9ca3af" />
                        <span style={{ fontSize: "12px", color: "#374151" }}>
                          {location.rating.toFixed(1)}
                          {location.user_ratings_total && (
                            <span style={{ color: "#9ca3af" }}> ({location.user_ratings_total.toLocaleString()})</span>
                          )}
                        </span>
                      </div>
                    )}
                    {location.open_now !== null && location.open_now !== undefined && (
                      <div className="d-flex align-items-center gap-1">
                        <div
                          style={{
                            width: "6px",
                            height: "6px",
                            borderRadius: "50%",
                            backgroundColor: location.open_now ? "#22c55e" : "#ef4444",
                          }}
                        />
                        <span style={{ fontSize: "12px", color: "#374151" }}>
                          {location.open_now ? "Open" : "Closed"}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Tags Row */}
                  {(location.is_fashion_retail || location.traffic_level || location.confidence || location.price_level) && (
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {location.confidence && (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            backgroundColor: "#f5f5f5",
                            color: "#666",
                            textTransform: "capitalize",
                          }}
                        >
                          {location.confidence}
                        </span>
                      )}
                      {location.is_fashion_retail && (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            backgroundColor: "#f5f5f5",
                            color: "#666",
                          }}
                        >
                          Fashion
                        </span>
                      )}
                      {location.traffic_level && (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            backgroundColor: "#f5f5f5",
                            color: "#666",
                            textTransform: "capitalize",
                          }}
                        >
                          {location.traffic_level} traffic
                        </span>
                      )}
                      {location.price_level && (
                        <span
                          style={{
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "3px",
                            backgroundColor: "#f5f5f5",
                            color: "#666",
                          }}
                        >
                          {"$".repeat(location.price_level)}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Recommendation */}
                  {location.recommendation && (
                    <div
                      style={{
                        padding: "10px 12px",
                        backgroundColor: "#fafafa",
                        borderRadius: "6px",
                        marginBottom: "12px",
                      }}
                    >
                      <span style={{ fontSize: "10px", color: "#9ca3af", textTransform: "uppercase", fontWeight: 500, letterSpacing: "0.5px" }}>
                        Recommendation
                      </span>
                      <p style={{ fontSize: "12px", color: "#4b5563", margin: "4px 0 0 0", lineHeight: "1.5" }}>
                        {location.recommendation}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="d-flex gap-2">
                    {location.google_maps_url && (
                      <a
                        href={location.google_maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "6px 12px",
                          backgroundColor: "#1a1a1a",
                          color: "#fff",
                          borderRadius: "6px",
                          fontSize: "11px",
                          textDecoration: "none",
                          fontWeight: 500,
                          transition: "background-color 0.15s ease",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#333"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#1a1a1a"}
                      >
                        <Navigation size={12} />
                        Directions
                      </a>
                    )}
                    {location.website && (
                      <a
                        href={location.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "6px 12px",
                          backgroundColor: "#fff",
                          color: "#374151",
                          borderRadius: "6px",
                          fontSize: "11px",
                          textDecoration: "none",
                          fontWeight: 500,
                          border: "1px solid #e5e7eb",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f9fafb";
                          e.currentTarget.style.borderColor = "#d1d5db";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#fff";
                          e.currentTarget.style.borderColor = "#e5e7eb";
                        }}
                      >
                        <ExternalLink size={12} />
                        Website
                      </a>
                    )}
                  </div>
                </div>

                {/* Location Footer with Coordinates */}
                {(location.latitude && location.longitude) && (
                  <div
                    style={{
                      padding: "8px 16px",
                      backgroundColor: "#fafafa",
                      borderTop: "1px solid #f5f5f5",
                      fontSize: "10px",
                      color: "#9ca3af",
                      fontFamily: "monospace",
                    }}
                  >
                    {location.latitude?.toFixed(6)}, {location.longitude?.toFixed(6)}
                  </div>
                )}
              </div>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderBrandsSection = (brandsData) => {
    if (!brandsData || (typeof brandsData === "object" && Object.keys(brandsData).length === 0)) {
      return (
        <div className="text-center py-4">
          <Info size={48} color="#ccc" />
          <p style={{ color: "#999", marginTop: "12px" }}>No brands data available</p>
        </div>
      );
    }

    // Get main level entries (excluding brands and more_brand_names)
    const mainEntries = Object.entries(brandsData).filter(([key]) => !brandSkipKeys.includes(key));

    // Get main brands array
    const mainBrands = brandsData.brands || [];

    // Get more_brand_names data
    const moreBrandNames = brandsData.more_brand_names;
    const moreBrandNamesEntries = moreBrandNames
      ? Object.entries(moreBrandNames).filter(([key]) => key !== "brands")
      : [];
    const moreBrandNamesArray = moreBrandNames?.brands || [];

    return (
      <div>
        {/* Main level metadata grid */}
        {renderMetadataGrid(mainEntries, brandFullWidthKeys)}

        {/* Main Brands List */}
        {renderBrandsList(mainBrands, "Brands")}

        {/* More Brand Names Subsection */}
        {moreBrandNames && (
          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "8px",
              padding: "16px",
              marginTop: "16px",
            }}
          >
            <div style={{ fontSize: "14px", color: "#333", fontWeight: 600, marginBottom: "16px", borderBottom: "1px solid #e0e0e0", paddingBottom: "8px" }}>
              More Brand Names
            </div>

            {/* More Brand Names metadata */}
            {renderMetadataGrid(moreBrandNamesEntries, brandFullWidthKeys)}

            {/* More Brand Names brands list */}
            {renderBrandsList(moreBrandNamesArray, "Brands")}
          </div>
        )}
      </div>
    );
  };
  return (
    <div className="mt-3">
      {/* Website Status Card */}
      <div
        className="mb-3 p-4 bg-white"
        style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: "12px" }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                backgroundColor: "#f0f4ff",
                borderRadius: "10px",
                padding: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Globe size={24} color="#508CFF" />
            </div>
            <div>
              <h5 style={{ marginBottom: "4px", fontWeight: 600 }}>Website Status</h5>
              <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                Current status of the {type} website
              </p>
            </div>
          </div>
          {/* Show badge on right side only for short status codes (length <= 3) */}
          {profile?.website_status && profile.website_status.length <= 3 && (
            <Badge color="primary" style={{ fontSize: "14px" }}>{profile.website_status}</Badge>
          )}
          {!profile?.website_status && (
            <Badge color="secondary">Not available</Badge>
          )}
        </div>
        {/* Show longer messages on new line */}
        {profile?.website_status && profile.website_status.length > 3 && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px",
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              fontSize: "14px",
              color: "#333",
              lineHeight: "1.5",
              wordBreak: "break-word",
            }}
          >
            {profile.website_status}
          </div>
        )}
      </div>

      {/* New Algorithm Card (Retailer Only) */}
      {type === "retailer" && (
        <div
          className="mb-3 p-4 bg-white"
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: "12px" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  backgroundColor: profile?.new_algorithm ? "#e6f7ee" : "#f5f5f5",
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Info size={24} color={profile?.new_algorithm ? "#28a745" : "#666"} />
              </div>
              <div>
                <h5 style={{ marginBottom: "4px", fontWeight: 600 }}>New Algorithm</h5>
                <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                  Indicates if new matching algorithm is applied
                </p>
              </div>
            </div>
            {profile?.new_algorithm ? (
              <Badge color="success" style={{ padding: "8px 16px", display: 'flex', alignItems: 'center', fontSize: "14px" }}>
                <CheckCircle size={14} className="me-1" /> <p className="m-0">Yes</p>
              </Badge>
            ) : (
              <Badge color="secondary" style={{ padding: "8px 16px", display: 'flex', alignItems: 'center', fontSize: "14px" }}>
                <XCircle size={14} className="me-1" /> <p className="m-0">No</p>
              </Badge>
            )}
          </div>
        </div>
      )}

      {/* Aggregate Card (Retailer Only) */}
      {type === "retailer" && profile?.aggregate && (
        <div
          className="mb-3 p-4 bg-white"
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: "12px" }}
        >
          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              style={{
                backgroundColor: "#e8f5e9",
                borderRadius: "10px",
                padding: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Info size={24} color="#4caf50" />
            </div>
            <div>
              <h5 style={{ marginBottom: "4px", fontWeight: 600 }}>Aggregate</h5>
              <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                Aggregation settings for this retailer
              </p>
            </div>
          </div>
          <Row>
            {Object.entries(profile.aggregate).map(([key, value], index) => (
              <Col md={6} key={index} className="mb-2">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 12px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "6px",
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#333", fontWeight: 500 }}>
                    {formatKey(key)}
                  </span>
                  <Badge color={value ? "success" : "secondary"} style={{ fontSize: "12px" }}>
                    {value ? "Yes" : "No"}
                  </Badge>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      )}

      {/* Summary Section */}
      <div
        className="mb-3 bg-white"
        style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: "12px", overflow: "hidden" }}
      >
        <div
          className="p-4 d-flex align-items-center justify-content-between"
          style={{ cursor: "pointer", borderBottom: expandedSections.summary ? "1px solid #e9ecef" : "none" }}
          onClick={() => toggleSection("summary")}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                backgroundColor: "#fff3e0",
                borderRadius: "10px",
                padding: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Info size={24} color="#ff9800" />
            </div>
            <div>
              <h5 style={{ marginBottom: "4px", fontWeight: 600 }}>Summary</h5>
              <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                Detailed summary information for this {type}
              </p>
            </div>
          </div>
          {expandedSections.summary ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
        </div>
        <Collapse isOpen={expandedSections.summary}>
          <div className="p-4" style={{ backgroundColor: "#fafafa" }}>
            {renderSummarySection(profile?.summary)}
          </div>
        </Collapse>
      </div>

      {/* Brands Section (Retailer Only) */}
      {type === "retailer" && (
        <div
          className="mb-3 bg-white"
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: "12px", overflow: "hidden" }}
        >
          <div
            className="p-4 d-flex align-items-center justify-content-between"
            style={{ cursor: "pointer", borderBottom: expandedSections.brands ? "1px solid #e9ecef" : "none" }}
            onClick={() => toggleSection("brands")}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  backgroundColor: "#e8f5e9",
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Info size={24} color="#4caf50" />
              </div>
              <div>
                <h5 style={{ marginBottom: "4px", fontWeight: 600 }}>All working with brands list </h5>
                <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                  Associated brands data for this retailer
                </p>
              </div>
            </div>
            {expandedSections.brands ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
          </div>
          <Collapse isOpen={expandedSections.brands}>
            <div className="p-4" style={{ backgroundColor: "#fafafa" }}>
              {renderBrandsSection(profile?.brands)}
            </div>
          </Collapse>
        </div>
      )}
      {/* Store Locations Section */}
      <div
        className="mb-3 bg-white"
        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.06)", borderRadius: "10px", overflow: "hidden", border: "1px solid #eaeaea" }}
      >
        <div
          className="p-4 d-flex align-items-center justify-content-between"
          style={{ cursor: "pointer", borderBottom: expandedSections.locations ? "1px solid #f0f0f0" : "none" }}
          onClick={() => toggleSection("locations")}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              style={{
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                padding: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MapPin size={20} color="#6b7280" />
            </div>
            <div>
              <h5 style={{ marginBottom: "2px", fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>
                Store Locations
                {storeLocations && storeLocations.length > 0 && (
                  <span
                    className="ms-2"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#6b7280",
                      backgroundColor: "#f5f5f5",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      verticalAlign: "middle",
                    }}
                  >
                    {storeLocations.length}
                  </span>
                )}
              </h5>
              <p style={{ margin: 0, color: "#9ca3af", fontSize: "13px" }}>
                Physical store locations for this {type}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {storeLocations && storeLocations.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleFetchLocations();
                }}
                disabled={fetchingLocations}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 12px",
                  backgroundColor: "#fff",
                  color: "#374151",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: fetchingLocations ? "not-allowed" : "pointer",
                  opacity: fetchingLocations ? 0.6 : 1,
                  transition: "all 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  if (!fetchingLocations) {
                    e.currentTarget.style.backgroundColor = "#f9fafb";
                    e.currentTarget.style.borderColor = "#d1d5db";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#fff";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }}
              >
                {fetchingLocations ? (
                  <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                ) : (
                  <>
                    <MapPin size={12} />
                    Refresh
                  </>
                )}
              </button>
            )}
            {expandedSections.locations ? <ChevronUp size={18} color="#9ca3af" /> : <ChevronDown size={18} color="#9ca3af" />}
          </div>
        </div>
        <Collapse isOpen={expandedSections.locations}>
          <div className="p-4" style={{ backgroundColor: "#fafafa" }}>
            {renderStoreLocationsSection()}
          </div>
        </Collapse>
      </div>

      {/* Sources Section */}
      {sources?.length > 0 && (
        <div
          className="mb-3 bg-white"
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.08)", borderRadius: "12px", overflow: "hidden" }}
        >
          <div
            className="p-4 d-flex align-items-center justify-content-between"
            style={{ cursor: "pointer", borderBottom: expandedSections.sources ? "1px solid #e9ecef" : "none" }}
            onClick={() => toggleSection("sources")}
          >
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  backgroundColor: "#e8f5e9",
                  borderRadius: "10px",
                  padding: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Info size={24} color="#4caf50" />
              </div>
              <div>
                <h5 style={{ marginBottom: "4px", fontWeight: 600 }}>Sources</h5>
                <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                  Associated sources data for this {type}
                </p>
              </div>
            </div>
            {expandedSections.sources ? <ChevronUp size={20} color="#666" /> : <ChevronDown size={20} color="#666" />}
          </div>
          <Collapse isOpen={expandedSections.sources}>
            <div className="p-4" style={{ backgroundColor: "#fafafa" }}>
              {renderSourcesSection(sources)}
            </div>
          </Collapse>
        </div>
      )}
    </div>
  );
};

export default ProfileMoreInfoTab;
